import type { GiftBatchStatus, GiftCouponStatus, GiftHistoryDisplayStatus } from '../../types/gifticon';

export type HistoryStatusLabel =
    | '발송 완료'
    | '사용 완료'
    | '예약 대기'
    | '결제 대기'
    | '결제 만료'
    | '결제 실패'
    | '발급 대기'
    | '발급 실패'
    | '전달 실패'
    | '취소 처리 중'
    | '취소됨'
    | '기간 만료';

export type HistoryStatusTone = 'blue' | 'green' | 'orange' | 'gray' | 'red';

export const HISTORY_DISPLAY_STATUS_LABELS: Record<GiftHistoryDisplayStatus, HistoryStatusLabel> = {
    PAYMENT_PENDING: '결제 대기',
    PAYMENT_EXPIRED: '결제 만료',
    PAYMENT_FAILED: '결제 실패',
    ISSUE_PENDING: '발급 대기',
    ISSUE_FAILED: '발급 실패',
    DELIVERY_FAILED: '전달 실패',
    SENT: '발송 완료',
    USED: '사용 완료',
    CANCELING: '취소 처리 중',
    CANCELED: '취소됨',
    EXPIRED: '기간 만료',
};

const HISTORY_DISPLAY_STATUS_TONES: Record<GiftHistoryDisplayStatus, HistoryStatusTone> = {
    PAYMENT_PENDING: 'gray',
    PAYMENT_EXPIRED: 'gray',
    PAYMENT_FAILED: 'red',
    ISSUE_PENDING: 'orange',
    ISSUE_FAILED: 'red',
    DELIVERY_FAILED: 'red',
    SENT: 'blue',
    USED: 'green',
    CANCELING: 'orange',
    CANCELED: 'gray',
    EXPIRED: 'gray',
};

const HISTORY_DISPLAY_STATUS_GUIDANCE: Record<GiftHistoryDisplayStatus, string> = {
    PAYMENT_PENDING: '결제가 완료되지 않아 아직 쿠폰을 발급하지 않았어요.',
    PAYMENT_EXPIRED: '결제 없이 주문 유효 시간이 지나 쿠폰을 발급하지 않았어요.',
    PAYMENT_FAILED: '결제 승인에 실패해 쿠폰을 발급하지 않았어요.',
    ISSUE_PENDING: '결제 또는 포인트 처리가 완료되어 쿠폰 발급을 기다리고 있어요.',
    ISSUE_FAILED: '결제 후 쿠폰 발급에 실패했어요. 자동화 탭의 보류함에서 다시 보낼 수 있어요.',
    DELIVERY_FAILED: '쿠폰은 발급됐지만 앱 알림·채팅·알림톡 중 전달 채널에서 실패했어요.',
    SENT: '쿠폰 발급과 전달이 완료됐으며 아직 사용되지 않았어요.',
    USED: '받는 사람이 쿠폰을 사용했어요.',
    CANCELING: '쿠폰 취소 결과를 확인 중이에요.',
    CANCELED: '발급된 쿠폰이 취소됐어요.',
    EXPIRED: '발급된 쿠폰의 사용 기간이 만료됐어요.',
};

export const toHistoryStatusLabel = (
    displayStatus: GiftHistoryDisplayStatus | undefined,
): HistoryStatusLabel | undefined => displayStatus ? HISTORY_DISPLAY_STATUS_LABELS[displayStatus] : undefined;

/** displayStatus가 없는 구 상세 응답에서 확정된 쿠폰 상태만 복원한다. */
export const toLegacyDetailDisplayStatus = (
    couponStatus: GiftCouponStatus,
): GiftHistoryDisplayStatus | undefined => {
    switch (couponStatus) {
        case 'SENT': return 'SENT';
        case 'USED': return 'USED';
        case 'EXPIRED': return 'EXPIRED';
        case 'CANCELING': return 'CANCELING';
        case 'CANCELED': return 'CANCELED';
        case 'FAILED': return 'ISSUE_FAILED';
        default: return undefined;
    }
};

export const resolveHistoryStatusLabel = ({
    batchStatus,
    couponStatus,
    displayStatus,
}: {
    batchStatus: GiftBatchStatus;
    couponStatus: GiftCouponStatus;
    displayStatus?: GiftHistoryDisplayStatus;
}): HistoryStatusLabel => {
    // 예약 발송은 결제/발급 처리 전 상태보다 예약 시각이라는 의미가 우선한다.
    if (batchStatus === 'SCHEDULED') return '예약 대기';
    const displayLabel = toHistoryStatusLabel(displayStatus);
    if (displayLabel) return displayLabel;

    // 혼합 롤아웃 중 displayStatus가 없는 구 응답을 위한 호환 매핑.
    if (batchStatus === 'PENDING_PAYMENT') return '결제 대기';
    if (couponStatus === 'USED') return '사용 완료';
    if (couponStatus === 'FAILED') return '발급 실패';
    if (couponStatus === 'CANCELING') return '취소 처리 중';
    if (couponStatus === 'CANCELED') return '취소됨';
    if (couponStatus === 'EXPIRED') return '기간 만료';
    return '발송 완료';
};

export const getHistoryStatusTone = (
    displayStatus: GiftHistoryDisplayStatus | undefined,
    fallbackStatus: HistoryStatusLabel,
): HistoryStatusTone => {
    if (displayStatus) return HISTORY_DISPLAY_STATUS_TONES[displayStatus];
    if (fallbackStatus === '사용 완료') return 'green';
    if (fallbackStatus === '예약 대기' || fallbackStatus === '발급 대기' || fallbackStatus === '취소 처리 중') return 'orange';
    if (fallbackStatus === '발급 실패' || fallbackStatus === '결제 실패' || fallbackStatus === '전달 실패') return 'red';
    if (fallbackStatus === '발송 완료') return 'blue';
    return 'gray';
};

export const getHistoryStatusGuidance = (
    displayStatus: GiftHistoryDisplayStatus | undefined,
): string | null => displayStatus ? HISTORY_DISPLAY_STATUS_GUIDANCE[displayStatus] : null;

export const shouldKeepScheduledHistoryLabel = (
    historyStatus: HistoryStatusLabel,
    detailDisplayStatus: GiftHistoryDisplayStatus | undefined,
): boolean => historyStatus === '예약 대기'
    && (!detailDisplayStatus || detailDisplayStatus === 'ISSUE_PENDING');

const LOCKED_DISPLAY_STATUS_REASONS: Partial<Record<GiftHistoryDisplayStatus, string>> = {
    PAYMENT_PENDING: '결제가 완료되기 전에는 재발송하거나 쿠폰을 취소할 수 없어요.',
    PAYMENT_EXPIRED: '결제 만료 건은 발급된 쿠폰이 없어 재발송하거나 취소할 수 없어요.',
    PAYMENT_FAILED: '결제 실패 건은 발급된 쿠폰이 없어 재발송하거나 취소할 수 없어요.',
    ISSUE_PENDING: '쿠폰 발급이 끝나기 전에는 재발송하거나 취소할 수 없어요.',
    ISSUE_FAILED: '발급에 실패한 선물은 자동화 탭의 보류함에서 다시 보낼 수 있어요.',
    DELIVERY_FAILED: '현재 재발송 버튼은 실패한 앱 전달 채널을 재시도하지 않아, 운영 확인 후 처리해 주세요.',
    USED: '사용 완료된 쿠폰은 재발송하거나 취소할 수 없어요.',
    CANCELING: '쿠폰 취소 결과를 확인 중이에요.',
    CANCELED: '취소된 쿠폰은 재발송하거나 다시 취소할 수 없어요.',
    EXPIRED: '기간이 만료된 쿠폰은 재발송하거나 취소할 수 없어요.',
};

export const getLockedResendDisplayStatusReason = (
    displayStatus: GiftHistoryDisplayStatus | undefined,
): string | null => displayStatus ? LOCKED_DISPLAY_STATUS_REASONS[displayStatus] ?? null : null;

export const getLockedCancelDisplayStatusReason = (
    displayStatus: GiftHistoryDisplayStatus | undefined,
): string | null => {
    if (displayStatus === 'DELIVERY_FAILED') return null;
    return displayStatus ? LOCKED_DISPLAY_STATUS_REASONS[displayStatus] ?? null : null;
};
